import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import Button from '@katalon-studio/katalon-ui/v2/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import InfoIcon from '@mui/icons-material/Info';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import makeStyles from '@mui/styles/makeStyles';
import { fromAccounts, fromOrder, useAppDispatch } from '../../store';
import { LoadingProgress } from '../../layout';
import { useAccount } from '../../account';
import { useQuery, useNavigate } from '../../routes';
import { subscription } from '../../layout/routes';
import { Account } from '../../models';

const useStyle = makeStyles(theme => ({
  container: {
    maxWidth: 800,
    margin: '0 auto',
    padding: theme.spacing(6, 3),
  },
  title: {
    fontSize: 32,
    fontWeight: 600,
    color: theme.refV2.cat.purple[400],
    marginBottom: theme.spacing(4),
    textAlign: 'center',
  },
  infoBanner: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
  },
  accountTable: {
    marginBottom: theme.spacing(4),
  },
  addLicenseBtn: {
    backgroundColor: theme.refV2.cat.purple[400],
    color: theme.common.white,
    '&:hover': {
      backgroundColor: theme.refV2.cat.purple[800],
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.grey[400],
    },
  },
  linkedStatus: {
    backgroundColor: theme.palette.success.main,
    color: theme.common.white,
    padding: theme.spacing(1, 2),
    borderRadius: 6,
    fontWeight: 500,
    fontSize: 14,
    display: 'inline-block',
    whiteSpace: 'nowrap',
  },
  faqSection: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(3),
  },
  faqTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: theme.spacing(2.5),
  },
  faqItem: {
    marginBottom: theme.spacing(2),
  },
  faqQuestion: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  faqAnswer: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  dialogContent: {
    padding: theme.spacing(4),
  },
  dialogActions: {
    padding: theme.spacing(0, 4, 4, 4),
    gap: theme.spacing(1.5),
  },
}));

export const LandingPage = () => {
  const classes = useStyle();
  const dispatch = useAppDispatch();
  const selectAccounts = useSelector(fromAccounts.selectEmptyAccounts);
  const awsLinkedAccount = useSelector(fromAccounts.selectAwsLinkedAccount);
  const { loading } = useAccount();
  const { get } = useQuery();
  const { navigate, replaceQuery } = useNavigate();
  const [selectedAccount, setSelectedAccount] = useState<Account>();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [linkedAccountId, setLinkedAccountId] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const linkAccountLoading = useSelector(fromOrder.selectLoading);

  const handleShowConfirmation = (account: Account) => {
    if (linkedAccountId) return;
    setSelectedAccount(account);
    setConfirmationOpen(true);
  };

  const handleConfirmLink = async () => {
    if (!selectedAccount || awsLinkedAccount) return;

    const awsToken = get('x-amzn-marketplace-token');
    const decodedAwsToken = decodeURIComponent((awsToken || '').replaceAll(/\+/g, '%20')).replaceAll(' ', '+');
    if (awsToken) {
      try {
        const result = await dispatch(fromOrder.doCheckoutAws({
          accountId: selectedAccount.id,
          awsToken: decodedAwsToken,
        }));

        if (fromOrder.doCheckoutAws.fulfilled.match(result)) {
          setLinkedAccountId(selectedAccount.id);
          setConfirmationOpen(false);
          setShowSuccess(true);

          // Navigate to subscription page with the accountId
          navigate(subscription.path, replaceQuery({
            accountId: selectedAccount.id.toString(),
          }));
        } else {
          setConfirmationOpen(false);
          setShowError(true);
        }
      } catch (error) {
        setConfirmationOpen(false);
        setShowError(true);
      }
    }
  };

  useEffect(() => {
    const fetchInfo = async () => {
      await Promise.all([
        dispatch(fromAccounts.doGetAwsLinkedAccount()),
        dispatch(fromAccounts.doGetEmptyAccounts()),
      ]);
    };
    fetchInfo();
  }, []);

  useEffect(() => {
    // Handle single account selection and confirmation
    if (selectAccounts.length === 1 && !awsLinkedAccount) {
      setSelectedAccount(selectAccounts[0]);
      handleShowConfirmation(selectAccounts[0]);
    }

    // Build accounts list
    const accounts = awsLinkedAccount
      ? [awsLinkedAccount, ...selectAccounts]
      : selectAccounts;
    setAccounts(accounts);

    // Set linkedAccountId if awsLinkedAccount exists
    if (awsLinkedAccount) {
      setLinkedAccountId(awsLinkedAccount.id);
    }
  }, [selectAccounts, awsLinkedAccount]);

  return (loading || linkAccountLoading) ? <LoadingProgress fullscreen /> : (
    <>
      <Box className={classes.container}>
        <Typography className={classes.title}>
          <FormattedMessage id="page.aws.billing.title" />
        </Typography>

        <Alert
          severity="info"
          icon={<InfoIcon />}
          className={classes.infoBanner}
        >
          <FormattedMessage
            id="page.aws.billing.info.banner"
            values={{
              note: <strong><FormattedMessage id="page.aws.billing.info.banner.note" /></strong>,
            }}
          />
        </Alert>

        <Typography className={classes.sectionTitle}>
          <FormattedMessage id="page.aws.billing.section.title" />
        </Typography>

        <TableContainer component={Paper} className={classes.accountTable}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong><FormattedMessage id="page.aws.billing.table.account.name" /></strong></TableCell>
                <TableCell align="right"><strong><FormattedMessage id="page.aws.billing.table.actions" /></strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map(account => (
                <TableRow key={account.id}>
                  <TableCell
                    sx={{
                      color: linkedAccountId === account.id ? 'success.main' : 'primary.main',
                      fontWeight: 600,
                    }}
                  >
                    {account.name}
                  </TableCell>
                  <TableCell align="right">
                    {linkedAccountId === account.id ? (
                      <Box className={classes.linkedStatus}>
                        <FormattedMessage id="page.aws.billing.status.licenses.added" />
                      </Box>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        className={classes.addLicenseBtn}
                        disabled={linkedAccountId !== null}
                        onClick={() => handleShowConfirmation(account)}
                      >
                        <FormattedMessage id="page.aws.billing.button.add.licenses" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Paper className={classes.faqSection}>
          <Typography className={classes.faqTitle}>
            <FormattedMessage id="page.aws.billing.faq.title" />
          </Typography>

          <Box className={classes.faqItem}>
            <Typography className={classes.faqQuestion}>
              <FormattedMessage id="page.aws.billing.faq.q1" />
            </Typography>
            <Typography className={classes.faqAnswer}>
              <FormattedMessage
                id="page.aws.billing.faq.a1"
                values={{
                  answer: <strong><FormattedMessage id="page.aws.billing.faq.answer.prefix" /></strong>,
                }}
              />
            </Typography>
          </Box>

          <Box className={classes.faqItem}>
            <Typography className={classes.faqQuestion}>
              <FormattedMessage id="page.aws.billing.faq.q2" />
            </Typography>
            <Typography className={classes.faqAnswer}>
              <FormattedMessage
                id="page.aws.billing.faq.a2"
                values={{
                  answer: <strong><FormattedMessage id="page.aws.billing.faq.answer.prefix" /></strong>,
                }}
              />
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Dialog open={confirmationOpen} onClose={() => setConfirmationOpen(false)}>
        <DialogTitle sx={{ padding: theme => theme.spacing(4, 4, 2, 4) }}>
          <FormattedMessage id="page.aws.billing.modal.title" />
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography>
            <FormattedMessage
              id="page.aws.billing.modal.message"
              values={{ accountName: selectedAccount?.name }}
            />
            <br />
            <br />
            <FormattedMessage id="page.aws.billing.modal.warning" />
          </Typography>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setConfirmationOpen(false)}>
            <FormattedMessage id="page.aws.billing.modal.cancel" />
          </Button>
          <Button
            variant="contained"
            className={classes.addLicenseBtn}
            onClick={handleConfirmLink}
          >
            <FormattedMessage id="page.aws.billing.modal.confirm" />
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          <FormattedMessage id="page.aws.billing.success.message" />
        </Alert>
      </Snackbar>

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setShowError(false)}>
          <FormattedMessage id="page.aws.billing.error.message" />
        </Alert>
      </Snackbar>
    </>
  );
};
